import React, { useEffect, useRef, useState } from "react";
import { BadgeCheck, Upload, Loader2, Clock, XCircle } from "lucide-react";
import api from "../../api";
import { compressImage } from "../../utils/image";
import { qaDetailText } from "../../utils/qaError";
import { useLang } from "../../context/LanguageContext";
import { catLabel } from "../../utils/categoryLabel";
import { useToast } from "../../hooks/use-toast";
import { plausibleEvent } from "../../utils/track";

export const VerificationCard = () => {
  const { t } = useLang();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [state, setState] = useState(null);
  const [reg, setReg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/my/verification").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (file) => {
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", await compressImage(file));
      form.append("private", "true");
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/my/verification", { documentUrl: data.url, registrationNumber: reg });
      plausibleEvent("Verification Requested");
      toast({ title: t("vf.sent"), description: t("vf.sentMsg") });
      await load();
    } catch (e) {
      toast({ title: t("vf.failed"), description: qaDetailText(t, e.response?.data?.detail) || t("prc.tryAgain") });
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!state) return null;
  const req = state.request;
  const pending = req?.status === "pending";

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="verification-card">
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <BadgeCheck size={18} className={state.verified ? "text-[#16a34a]" : "text-muted-foreground"} />
        {t("vf.title")}
      </h2>

      {state.verified ? (
        <p className="text-sm text-muted-foreground mt-1" data-testid="verification-done">{t("vf.verifiedMsg")}</p>
      ) : pending ? (
        <p className="text-sm text-amber-700 mt-1 flex items-center gap-1.5" data-testid="verification-pending">
          <Clock size={14} /> {t("vf.pendingMsg")}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground mt-1">{t("vf.body")}</p>
          {state.domainVerified && (
            <p className="text-xs text-[#15803d] font-semibold mt-2 flex items-start gap-1.5" data-testid="domain-tier-note">
              <BadgeCheck size={13} className="mt-0.5 shrink-0" />
              <span>{t("vf.domainTier")} — {t("vf.domainTierNote", { domain: state.verifiedDomain || "your domain" })}</span>
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1" data-testid="verification-unlocks">{t("vf.unlocks", { deals: state.liveDealLimit ?? 2, pins: state.branchLimit ?? 1 })}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("vf.riskyNote", { list: (state.riskyCategories || []).map((c) => catLabel(c, t)).join(", ") })}</p>
          {req?.status === "rejected" && (
            <p className="text-xs text-rose-600 mt-2 flex items-center gap-1.5" data-testid="verification-rejected">
              <XCircle size={13} /> {req.reason || t("vf.rejected")}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <input value={reg} onChange={(e) => setReg(e.target.value)} data-testid="verification-reg-input"
              placeholder={t("vf.regPlaceholder")}
              className="flex-1 min-w-[180px] bg-secondary rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#16a34a]/40" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => e.target.files?.[0] && submit(e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="verification-upload-btn"
              className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-colors">
              {busy ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} {t("vf.upload")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default VerificationCard;
